import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { REVIEWS, GOOGLE_RATING, GOOGLE_REVIEWS_URL } from "./reviewsData";
import { EASE } from "./constants";

const GoogleG = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Stars = ({ n }) => (
  <div className="flex gap-0.5" aria-label={`${n} su 5 stelle`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={16} className={i < n ? "fill-[#21463E] text-[#21463E]" : "text-[#DBDBDA]"} />
    ))}
  </div>
);

export const Reviews = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-12%" });

  return (
    <section id="recensioni" ref={ref} data-testid="reviews-section" className="py-24 md:py-36 max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
        <div>
          <div className="overline text-[#21463E] mb-6 flex items-center gap-3">
            <span className="h-px w-8 bg-[#21463E] inline-block" /> Recensioni
          </div>
          <h2 className="font-serif font-light text-[#2E3131] text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight max-w-2xl">
            La fiducia di chi ci ha <em className="italic text-[#21463E]">già scelto.</em>
          </h2>
        </div>

        <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noreferrer" data-testid="reviews-google-link" className="group flex items-center gap-4 shrink-0 rounded-2xl border border-[#DBDBDA] bg-white px-5 py-4 hover:border-[#21463E]/40 hover:shadow-lg hover:shadow-[#2E3131]/5 transition-[border-color,box-shadow] duration-300">
          <GoogleG size={30} />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-serif text-3xl text-[#2E3131] leading-none">{GOOGLE_RATING.toFixed(1)}</span>
              <Stars n={5} />
            </div>
            <div className="text-sm text-[#5C6360] mt-1 group-hover:text-[#2E3131] transition-colors duration-300 flex items-center gap-1">
              Recensioni verificate su Google
              <span className="inline-block group-hover:translate-x-0.5 transition-transform duration-300">→</span>
            </div>
          </div>
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {REVIEWS.map((r, i) => (
          <motion.figure
            key={i}
            data-testid={`review-card-${i}`}
            initial={{ opacity: 0, y: 36 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }}
            className="group flex flex-col justify-between rounded-3xl border border-[#DBDBDA] bg-[#F5F6F2] p-8 hover:border-[#21463E]/40 hover:shadow-xl hover:shadow-[#2E3131]/5 transition-[border-color,box-shadow] duration-500"
          >
            <div>
              <Quote size={30} className="text-[#21463E]/30 mb-5 group-hover:text-[#21463E]/60 transition-colors duration-300" />
              <Stars n={r.stelle} />
              <blockquote className="mt-5 text-[#2E3131]/90 leading-relaxed">&ldquo;{r.testo}&rdquo;</blockquote>
            </div>
            <figcaption className="mt-8 flex items-center gap-3 pt-6 border-t border-[#DBDBDA]">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2E3131] text-[#F5F6F2] text-sm font-medium">{r.iniziali}</span>
              <div>
                <div className="text-sm font-medium text-[#2E3131]">{r.nome}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <GoogleG size={12} />
                <span className="overline text-[0.6rem] text-[#5C6360]">Recensione Google</span>
              </div>
              </div>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
};

export default Reviews;
